import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Card, CardContent, Grid, Button, CircularProgress, Chip,
} from '@mui/material';
import { Search, Send, Visibility, Business } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { companyService, DashboardData } from '../../services/companyService';
import { CompanyUserData } from '../../services/authService';

const CompanyDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  const companyUser = (user ?? {}) as Partial<CompanyUserData>;

  useEffect(() => {
    companyService.getDashboard()
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const stats = [
    { label: '公開プロフィール数', value: data?.totalProfiles ?? 0, icon: <Visibility />, color: '#0066cc' },
    { label: '送信スカウト数', value: data?.totalScouts ?? 0, icon: <Send />, color: '#ff9800' },
    { label: '未読返信', value: data?.unreadReplies ?? 0, icon: <Send />, color: '#f44336' },
    { label: '今月の閲覧数', value: data?.monthlyViews ?? 0, icon: <Visibility />, color: '#4caf50' },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <Box sx={{ bgcolor: '#0066cc', color: 'white', py: 3 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                企業ダッシュボード
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <Business sx={{ fontSize: 18 }} />
                <Typography variant="body2">{companyUser?.companyName}</Typography>
                <Chip label={data?.planState === 'free_beta' ? 'Free Beta' : data?.planState} size="small"
                  sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }} variant="outlined" />
              </Box>
            </Box>
            <Button variant="outlined" onClick={logout} sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}>
              ログアウト
            </Button>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* 統計カード */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {stats.map((stat) => (
            <Grid item xs={6} md={3} key={stat.label}>
              <Card>
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <Box sx={{ color: stat.color, mb: 1 }}>{stat.icon}</Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>{stat.value}</Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>{stat.label}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* アクションボタン */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ cursor: 'pointer', '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } }}
              onClick={() => navigate('/company/search')}>
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Search sx={{ fontSize: 48, color: '#0066cc', mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                  アスリート検索
                </Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  匿名公開されているアスリートのキャリアプロフィールを検索・閲覧できます
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ cursor: 'pointer', '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } }}
              onClick={() => navigate('/company/scouts')}>
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Send sx={{ fontSize: 48, color: '#ff9800', mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                  スカウト管理
                </Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  送信済みスカウトの状況確認・アスリートからの返信を確認できます
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default CompanyDashboardPage;
